import {
    Experience,
    GiftOffer,
    Membership,
    Merchandise,
    Package,
    PartnerPackage,
    Product,
    ProductObject,
    ProductReference,
} from "types";

enum PlaceholderImages {
    SMALL = "/cache/images/placeholder_140x105.jpg",
    MEDIUM = "/cache/images/placeholder_268x200.jpg",
    LARGE = "/cache/images/placeholder_723x542.jpg",
}

export const getBasePlaceholderImageUrl = (size = "medium") => {
    const placeholderSize = size.toUpperCase() as keyof typeof PlaceholderImages;
    // This only returns the relative path because `config.xola.url` is configured in the app, not common
    return PlaceholderImages[placeholderSize] ?? PlaceholderImages.MEDIUM;
};

/**
 *  Get Photo Url for all types of products (Experiences, Packages, Combos, Memberships)
 *  using either Product | { id: ID; name?: string } since other documents will just have reference of product
 */
export const getBaseDefaultPhotoUrl = (
    product: Product | ProductReference | Experience | GiftOffer | Membership | Merchandise,
    size = "medium",
) => {
    if (!product || !product.id) {
        return getBasePlaceholderImageUrl(size);
    }

    let productId = product.id;
    let object = "experiences";
    switch (product?.object) {
        case ProductObject.PACKAGE:
            object = "packages";
            if ("photo" in product) {
                // X2-2893: Fix for primary photo isn't updated (V1 uses the same approach instead of /default path)
                return `/${object}/${productId}/medias/${product.photo?.id}?size=${size}`;
            }
            break;
        case ProductObject.COMBO:
            object = "combos";
            break;
        case ProductObject.MEMBERSHIP:
            object = "memberships";
            if ("photo" in product) {
                return `/${object}/${productId}/medias/${product.photo?.id}?size=${size}`;
            }
            break;
        case ProductObject.WARE:
            object = "wares";
            if ("photo" in product) {
                return `/${object}/${productId}/medias/${product.photo?.id}?size=${size}`;
            }
            break;
        case ProductObject.GIFT_OFFER:
            if ("experience" in product) {
                productId = product.experience!.id;
                object = "experiences";
            } else {
                object = "giftOffers";
                if ("photo" in product) {
                    return `/${object}/${productId}/medias/${product.photo?.id}?size=${size}`;
                }
            }

            break;
        case ProductObject.EXPERIENCE:
            if ("photo" in product) {
                // X2-2893: Fix for primary photo isn't updated (V1 uses the same approach instead of /default path)
                return `/${object}/${productId}/medias/${product.photo?.id}?size=${size}`;
            }

            break;
        case ProductObject.PARTNER_PACKAGE:
            object = "partnerPackages";
            if ("photo" in product) {
                // X2-2893: Fix for primary photo isn't updated (V1 uses the same approach instead of /default path)
                return `/${object}/${productId}/medias/${product.photo?.id}?size=${size}`;
            }
            break;
        default:
            object = "experiences";
            break;
    }

    // This only returns the relative path because `config.xola.url` is configured in the app, not common
    return `/${object}/${productId}/medias/default?size=${size}`;
};

export const isExperienceProduct = (product?: Product | ProductReference): product is Experience => {
    return product?.object === ProductObject.EXPERIENCE;
};

export const isMerchandiseProduct = (product?: Product | ProductReference): product is Merchandise => {
    return product?.object === ProductObject.WARE;
};

export const isGiftOfferProduct = (product?: Product | ProductReference): product is GiftOffer => {
    return product?.object === ProductObject.GIFT_OFFER;
};

export const isPackageProduct = (product?: Product | ProductReference): product is Package => {
    return product?.object === ProductObject.PACKAGE;
};

export const isPartnerPackageProduct = (product?: Product | ProductReference): product is PartnerPackage => {
    return product?.object === ProductObject.PARTNER_PACKAGE;
};

export const isMembershipProduct = (product?: Product | ProductReference): product is Membership => {
    return product?.object === ProductObject.MEMBERSHIP;
};

export const isComboProduct = (product?: Product | ProductReference) => {
    return product?.object === ProductObject.COMBO;
};

export const isBaseGiftProduct = (product: Product) => {
    return (
        product.object === ProductObject.GIFT_OFFER &&
        product.baseGiftProduct &&
        product.system &&
        !("experience" in product)
    );
};

export const getProductObject = (product: Product | ProductReference) => {
    switch (product?.object) {
        case ProductObject.EXPERIENCE:
            return "experience";
        case ProductObject.MEMBERSHIP:
            return "membership";
        case ProductObject.PACKAGE:
            return "package";
        default:
            return "experience";
    }
};
